/**
 * Delete someone from the portal.
 *
 * Asks first, in words that say what survives: their shifts and documents are
 * working-time records the company keeps, so "delete" removes the person's access and
 * their place in every list, not their history.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useConfirm } from '@/components/ConfirmDialog';
import { useI18n } from '@/i18n';
import { api, ApiError, type User } from '@/lib/api';
import { fullName } from '@/lib/format';

export function DeleteUserButton({
  user,
  compact = false,
  onDeleted,
}: {
  user: User;
  compact?: boolean;
  onDeleted?: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const remove = useMutation({
    mutationFn: () => api.deleteUser(user.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onDeleted?.();
    },
    onError: (error) => {
      void confirm({ notice: true, message: error instanceof ApiError ? error.message : t('common.saveError') });
    },
  });

  const onClick = async () => {
    const message = t('access.deleteConfirm', { name: fullName(user), email: user.email ?? '-' });
    if (await confirm({ message, confirmLabel: t('common.delete'), danger: true })) remove.mutate();
  };

  return (
    <button
      type="button"
      disabled={remove.isPending}
      onClick={onClick}
      className={
        compact
          ? 'text-xs font-semibold text-muted hover:text-danger disabled:opacity-60'
          : 'btn-danger'
      }
    >
      {remove.isPending ? t('access.deleting') : t('access.delete')}
    </button>
  );
}
